import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { authService, RegisterRequest } from '../services/auth';
import { useAuthForm } from '../hooks/useAuthForm';
import { parseAuthError } from '../utils/authUtils';
import AuthPageLayout from '../components/ui/AuthPageLayout';
import EmailField from '../components/ui/EmailField';
import PasswordField from '../components/ui/PasswordField';
import FirstNameField from '../components/ui/FirstNameField';
import LastNameField from '../components/ui/LastNameField';
import AlertMessage from '../components/ui/AlertMessage';
import SubmitButton from '../components/ui/SubmitButton';
import AuthLink from '../components/ui/AuthLink';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const {
    formData,
    handleInputChange,
    isLoading,
    setIsLoading,
    error,
    setError,
    success,
    setSuccess,
  } = useAuthForm<RegisterRequest>({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    try {
      const response = await authService.register(formData);
      dispatch({ type: 'SET_USER', payload: response.user });
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = parseAuthError(
        err,
        'Registration failed. Please try again.'
      );
      setError(errorMessage);

      // Extract field-level errors from the response
      if (err.response?.data?.field_errors) {
        setFieldErrors(err.response.data.field_errors);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageLayout
      title="Create your account"
      description={
        <>
          Or <AuthLink to="/signin">sign in to your existing account</AuthLink>
        </>
      }
    >
      <form className="space-y-6" onSubmit={handleSubmit}>
        <AlertMessage type="error" message={error} show={!!error} />
        <AlertMessage type="success" message={success} show={!!success} />

        <FirstNameField
          id="first_name"
          name="first_name"
          label="First Name"
          placeholder="Enter your first name"
          value={formData.first_name}
          onChange={handleInputChange}
          required
          autoComplete="given-name"
          serverErrors={fieldErrors.first_name}
        />

        <LastNameField
          id="last_name"
          name="last_name"
          label="Last Name"
          placeholder="Enter your last name"
          value={formData.last_name}
          onChange={handleInputChange}
          autoComplete="family-name"
        />

        <EmailField
          id="email"
          name="email"
          label="Email address"
          placeholder="Enter your email"
          value={formData.email}
          onChange={handleInputChange}
          required
          autoComplete="email"
          serverErrors={fieldErrors.email}
        />

        <PasswordField
          id="password"
          name="password"
          label="Password"
          placeholder="Enter your password"
          value={formData.password}
          onChange={handleInputChange}
          required
          autoComplete="new-password"
          serverErrors={fieldErrors.password}
        />

        <SubmitButton isLoading={isLoading}>Create account</SubmitButton>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                Or continue with
              </span>
            </div>
          </div>

          <GoogleSignInButton mode="signup" onError={setError} />
        </div>

        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
          By creating an account, you agree to our{' '}
          <AuthLink to="/terms" className="text-xs">
            Terms of Service
          </AuthLink>{' '}
          and{' '}
          <AuthLink to="/privacy" className="text-xs">
            Privacy Policy
          </AuthLink>
        </div>
      </form>
    </AuthPageLayout>
  );
};

export default SignUp;
